// Catalogue calibré de la branche vitrine : lots, heures, montants, formules
// après mise en ligne. SEULE source des chiffres affichés sur la page vitrine :
// l'IA peut choisir des lots, jamais écrire une heure ou un montant.
//
// PREMIER JET (2026-09-27), rédigé par Claude à la demande de Thibault, À
// CORRIGER par Thibault. Montants en euros, sans TVA (franchise en base, art.
// 293 B du CGI). Les montants ne sont volontairement pas proportionnels aux
// heures : ils reflètent la valeur du poste, pour qu'aucun taux horaire unique
// ne soit calculable depuis la grille (OFFRE_VITRINE.md : "jamais de taux
// horaire").
//
// Les lignes d'une page déjà générée sont figées au moment de la génération
// (voir VitrinePricingLine) : corriger ce fichier ne change pas les offres
// déjà envoyées, seulement les suivantes.

export type VitrineCatalogueKind = "standard" | "wordpress";

export interface CatalogueLot {
  id: string;
  label: string;
  description: string; // ce qu'inclut le lot, en mots de client
  minutes: number; // temps de travail réel estimé, prudent, par unité
  montant: number; // euros par unité (0 = inclus)
  unite?: "page" | "correction"; // lot facturé à l'unité (quantité > 1 possible)
}

export const CATALOGUE_STANDARD: CatalogueLot[] = [
  { id: "cadrage", label: "Cadrage et arborescence", description: "On fixe ensemble les pages, leur contenu et le parcours du visiteur.", minutes: 90, montant: 120 },
  { id: "maquette-accueil", label: "Maquette de la page d'accueil", description: "La page d'accueil dessinée avant tout développement, deux séries de corrections incluses.", minutes: 180, montant: 250 },
  { id: "integration-accueil", label: "Page d'accueil", description: "La page d'accueil construite d'après la maquette validée, adaptée au mobile.", minutes: 240, montant: 290 },
  { id: "page-contenu", label: "Page de contenu", description: "Une page du site mise en forme (présentation, service, contact...).", minutes: 60, montant: 70, unite: "page" },
  { id: "back-office", label: "Espace d'administration", description: "Vous modifiez vous-même textes, images, pages et articles.", minutes: 120, montant: 190 },
  { id: "formulaire-contact", label: "Formulaire de contact", description: "Formulaire avec protection anti-spam, messages reçus par e-mail.", minutes: 45, montant: 60 },
  { id: "formulaire-metier", label: "Formulaire métier", description: "Formulaire avec vos champs métier, anti-spam, conforme RGPD, notification par e-mail.", minutes: 150, montant: 240 },
  { id: "estimateur", label: "Estimateur en ligne", description: "Quelques questions au visiteur, une estimation selon un barème que vous fixez.", minutes: 240, montant: 340 },
  { id: "galerie", label: "Galerie de réalisations", description: "Vos réalisations en photos, que vous alimentez vous-même.", minutes: 90, montant: 110 },
  { id: "carte", label: "Carte ou annuaire des établissements", description: "Vos adresses ou votre zone d'intervention sur une carte, avec recherche.", minutes: 150, montant: 200 },
  { id: "blog", label: "Actualités ou blog", description: "Page d'archive et modèle d'article, que vous publiez vous-même.", minutes: 120, montant: 150 },
  { id: "seo-base", label: "Référencement naturel de base", description: "Titres, descriptions, structure des pages, plan du site, données structurées, vitesse.", minutes: 90, montant: 130 },
  { id: "seo-local", label: "Référencement local", description: "Fiche Google Business Profile, cohérence de l'adresse partout, balisage local.", minutes: 90, montant: 120 },
  { id: "visibilite-ia", label: "Visibilité dans les assistants IA", description: "Contenu en questions-réponses et données structurées lisibles par ChatGPT et Google.", minutes: 60, montant: 80 },
  { id: "reprise-contenu", label: "Reprise de contenu de l'ancien site", description: "Textes et images repris de votre site actuel.", minutes: 30, montant: 30, unite: "page" },
  { id: "redirections", label: "Redirections de l'ancien site", description: "Les anciennes adresses renvoient vers les nouvelles pages, sans perte de référencement.", minutes: 60, montant: 70 },
  { id: "legal", label: "Mentions légales et RGPD", description: "Mentions légales, politique de confidentialité, bandeau cookies.", minutes: 45, montant: 60 },
  { id: "mise-en-ligne", label: "Mise en ligne", description: "Nom de domaine, certificat de sécurité, sauvegardes en place.", minutes: 60, montant: 80 },
  { id: "prise-en-main", label: "Prise en main et aide-mémoire", description: "Une heure en visio pour apprendre à modifier votre site, et un aide-mémoire écrit.", minutes: 90, montant: 0 },
];

export const CATALOGUE_WORDPRESS: CatalogueLot[] = [
  { id: "wp-audit", label: "Audit du site", description: "Thème, extensions, vitesse et sécurité passés en revue avant d'intervenir.", minutes: 90, montant: 110 },
  { id: "wp-sauvegarde-preprod", label: "Sauvegarde et site de test", description: "Sauvegarde complète, copie du site sur un sous-domaine protégé et non indexé.", minutes: 60, montant: 70 },
  { id: "wp-mises-a-jour", label: "Mises à jour", description: "WordPress, thème et extensions mis à jour et testés.", minutes: 90, montant: 100 },
  { id: "wp-nettoyage", label: "Nettoyage des extensions", description: "Les extensions inutiles ou en double retirées.", minutes: 60, montant: 80 },
  { id: "wp-arborescence", label: "Menu et arborescence", description: "Menu simplifié et pages réorganisées.", minutes: 60, montant: 80 },
  { id: "wp-accueil", label: "Refonte de la page d'accueil", description: "La page d'accueil refaite dans votre constructeur de pages.", minutes: 240, montant: 300 },
  { id: "wp-page", label: "Création ou refonte d'une page", description: "Une page créée ou reprise dans votre constructeur.", minutes: 75, montant: 85, unite: "page" },
  { id: "wp-section", label: "Ajout ou reprise d'une section", description: "Une section ajoutée ou refaite sur une page existante (témoignages, bandeau, logos...).", minutes: 45, montant: 55, unite: "correction" },
  { id: "wp-correction", label: "Correction ponctuelle", description: "Un défaut précis corrigé (lien cassé, affichage mobile, contraste...).", minutes: 20, montant: 25, unite: "correction" },
  { id: "wp-formulaire", label: "Formulaire qualifié", description: "Formulaire avec vos champs métier et notification par e-mail.", minutes: 120, montant: 180 },
  { id: "wp-performance", label: "Vitesse de chargement", description: "Images compressées, cache, chargement allégé.", minutes: 120, montant: 150 },
  { id: "wp-seo-base", label: "Référencement naturel de base", description: "Titres, descriptions, structure des titres, textes alternatifs.", minutes: 90, montant: 120 },
  { id: "wp-seo-local", label: "Référencement local", description: "Fiche Google Business Profile et balisage local.", minutes: 90, montant: 120 },
  { id: "wp-blog", label: "Blog", description: "Page d'archive et modèle d'article.", minutes: 90, montant: 120 },
  { id: "wp-mise-en-production", label: "Mise en production", description: "Passage du site de test au site réel, vérifications finales.", minutes: 60, montant: 70 },
  { id: "wp-prise-en-main", label: "Prise en main et aide-mémoire", description: "Une heure en visio sur les modifications courantes, et un aide-mémoire écrit.", minutes: 90, montant: 0 },
];

// Option proposée (jamais en base) quand les visiteurs visés par le site sont
// des professionnels (demande Thibault 2026-09-27), en variante standard comme
// WordPress. Montant et temps : premier jet à corriger. Mention RGPD : la
// prospection B2B par e-mail est permise si elle concerne la fonction de la
// personne, avec source des données et droit d'opposition.
export const LOT_PROSPECTS_B2B: CatalogueLot = {
  id: "prospects-b2b",
  label: "Liste de 500 prospects qualifiés",
  description:
    "500 décideurs de votre cible (nom, prénom, poste, e-mail professionnel vérifié), prêts à contacter. Sources indiquées, dans le cadre du RGPD pour la prospection entre professionnels.",
  minutes: 180,
  montant: 290,
};

export function getCatalogue(kind: VitrineCatalogueKind): CatalogueLot[] {
  return kind === "wordpress" ? CATALOGUE_WORDPRESS : CATALOGUE_STANDARD;
}

// Lots toujours présents dans la grille (le reste dépend du brief).
export const LOTS_OBLIGATOIRES: Record<VitrineCatalogueKind, string[]> = {
  standard: [
    "cadrage",
    "maquette-accueil",
    "integration-accueil",
    "back-office",
    "seo-base",
    "visibilite-ia",
    "legal",
    "mise-en-ligne",
    "prise-en-main",
  ],
  wordpress: ["wp-audit", "wp-sauvegarde-preprod", "wp-mise-en-production", "wp-prise-en-main"],
};

// Jalons de paiement (palier trafic supprimé pour la vitrine, décision
// 2026-09-27) : l'étape déclenchant les 20 % dépend de la variante.
export const JALONS: Record<VitrineCatalogueKind, { part: number; label: string }[]> = {
  standard: [
    { part: 30, label: "À la commande" },
    { part: 20, label: "À la validation de la maquette" },
    { part: 50, label: "À la mise en ligne, une fois le site validé par vous" },
  ],
  wordpress: [
    { part: 30, label: "À la commande" },
    { part: 20, label: "À la validation du plan d'intervention, après l'audit" },
    { part: 50, label: "À la mise en production, une fois le site de test validé par vous" },
  ],
};

// Support offert après la mise en ligne : 1 mois pour la vitrine (décision
// Thibault 2026-09-27 ; la page SaaS en offre 3).
export const SUPPORT_GRATUIT_MOIS = 1;
export const SUPPORT_INCLUS = "Corrections, petites modifications, questions sur l'administration du site.";

// Après le support offert : la maintenance à l'heure de la page SaaS
// (how-it-works.tsx, "Maintenance à l'heure"), reprise telle quelle à la
// demande de Thibault (2026-09-27), vocabulaire adapté à un site vitrine.
export const MAINTENANCE_HORAIRE = {
  tarifHeure: 40,
  inclus: {
    standard: [
      "Vous choisissez le nombre d'heures forfaitaires par mois",
      "Modifications, corrections et évolutions du site, selon vos priorités",
      "Réponse sous 24 h pour un problème urgent",
      "Heures non utilisées cumulables sur le mois suivant : ce n'est pas un capital qui se perd",
      "Pour une demande estimée à plus de 4 h, je vous préviens avant de commencer pour qu'on valide ensemble la priorité et les heures que ça consommera",
    ],
    wordpress: [
      "Vous choisissez le nombre d'heures forfaitaires par mois",
      "Mises à jour de WordPress, du thème et des extensions testées avant application, corrections et modifications, selon vos priorités",
      "Réponse sous 24 h pour un problème urgent",
      "Heures non utilisées cumulables sur le mois suivant : ce n'est pas un capital qui se perd",
      "Pour une demande estimée à plus de 4 h, je vous préviens avant de commencer pour qu'on valide ensemble la priorité et les heures que ça consommera",
    ],
  } satisfies Record<VitrineCatalogueKind, string[]>,
  engagement:
    "Si une intervention prend plus de temps que l'estimation que je vous ai annoncée, je la termine sans facturer ni décompter les heures en trop.",
};

// Hébergement du site par Léman IT (variante standard uniquement : en
// variante WordPress, le client garde son hébergeur).
export const HEBERGEMENT = {
  montantMensuel: 15,
  contenu: "Hébergement sur serveur maîtrisé, certificat de sécurité, sauvegardes quotidiennes, mises à jour de sécurité.",
};

export function formatMinutes(min: number): string {
  if (min < 60) return `${min} min`;
  const h = Math.floor(min / 60);
  const m = min % 60;
  return m === 0 ? `${h}h` : `${h}h${String(m).padStart(2, "0")}`;
}

export function formatEuros(n: number): string {
  return `${new Intl.NumberFormat("fr-FR").format(Math.round(n))} €`;
}

// ---------------------------------------------------------------------------
// Version anglaise (pages vitrine et prospection en anglais, 2026-10-15).
// Mêmes ids, mêmes heures et montants : seuls les textes changent. Les lignes
// d'une page sont figées à la génération dans la langue de la page.
// ---------------------------------------------------------------------------

export type VitrineLanguage = "fr" | "en";

export const LOTS_EN: Record<string, { label: string; description: string }> = {
  cadrage: { label: "Scoping and site structure", description: "Together we set the pages, their content and the visitor's journey." },
  "maquette-accueil": { label: "Home page mockup", description: "The home page designed before any development, with two rounds of changes included." },
  "integration-accueil": { label: "Home page", description: "The home page built from the approved mockup, adapted to mobile." },
  "page-contenu": { label: "Content page", description: "A page of the site laid out (about, service, contact...)." },
  "back-office": { label: "Admin area", description: "You edit texts, images, pages and articles yourself." },
  "formulaire-contact": { label: "Contact form", description: "Form with spam protection, messages delivered by email." },
  "formulaire-metier": { label: "Business form", description: "Form with your business fields, spam protection, GDPR-compliant, email notification." },
  estimateur: { label: "Online estimator", description: "A few questions for the visitor, and an estimate based on a rate card you set." },
  galerie: { label: "Project gallery", description: "Your work in photos, which you add to yourself." },
  carte: { label: "Map or directory of locations", description: "Your addresses or service area on a map, with search." },
  blog: { label: "News or blog", description: "Archive page and article template, which you publish yourself." },
  "seo-base": { label: "Basic search engine optimisation", description: "Titles, descriptions, page structure, sitemap, structured data, speed." },
  "seo-local": { label: "Local SEO", description: "Google Business Profile listing, consistent address everywhere, local markup." },
  "visibilite-ia": { label: "Visibility in AI assistants", description: "Question-and-answer content and structured data readable by ChatGPT and Google." },
  "reprise-contenu": { label: "Content transfer from the old site", description: "Texts and images taken over from your current site." },
  redirections: { label: "Redirects from the old site", description: "Old addresses point to the new pages, with no loss of search ranking." },
  legal: { label: "Legal notice and GDPR", description: "Legal notice, privacy policy, cookie banner." },
  "mise-en-ligne": { label: "Go-live", description: "Domain name, security certificate, backups in place." },
  "prise-en-main": { label: "Onboarding and quick guide", description: "One hour by video call to learn how to edit your site, and a written quick guide." },
  "wp-audit": { label: "Site audit", description: "Theme, plugins, speed and security reviewed before any work." },
  "wp-sauvegarde-preprod": { label: "Backup and staging site", description: "Full backup, copy of the site on a protected, non-indexed subdomain." },
  "wp-mises-a-jour": { label: "Updates", description: "WordPress, theme and plugins updated and tested." },
  "wp-nettoyage": { label: "Plugin clean-up", description: "Unused or duplicate plugins removed." },
  "wp-arborescence": { label: "Menu and site structure", description: "Simplified menu and reorganised pages." },
  "wp-accueil": { label: "Home page redesign", description: "The home page rebuilt in your page builder." },
  "wp-page": { label: "Page creation or redesign", description: "A page created or reworked in your page builder." },
  "wp-section": { label: "Section added or reworked", description: "A section added or redone on an existing page (testimonials, banner, logos...)." },
  "wp-correction": { label: "One-off fix", description: "A specific issue fixed (broken link, mobile display, contrast...)." },
  "wp-formulaire": { label: "Qualified form", description: "Form with your business fields and email notification." },
  "wp-performance": { label: "Loading speed", description: "Compressed images, caching, lighter loading." },
  "wp-seo-base": { label: "Basic search engine optimisation", description: "Titles, descriptions, heading structure, alt texts." },
  "wp-seo-local": { label: "Local SEO", description: "Google Business Profile listing and local markup." },
  "wp-blog": { label: "Blog", description: "Archive page and article template." },
  "wp-mise-en-production": { label: "Go-live", description: "Moving from the staging site to the live site, final checks." },
  "wp-prise-en-main": { label: "Onboarding and quick guide", description: "One hour by video call on everyday edits, and a written quick guide." },
  "prospects-b2b": {
    label: "List of 500 qualified prospects",
    description:
      "500 decision-makers in your target market (first name, last name, job title, verified business email), ready to contact. Sources stated, within the GDPR framework for business-to-business prospecting.",
  },
};

export const JALONS_EN: Record<VitrineCatalogueKind, { part: number; label: string }[]> = {
  standard: [
    { part: 30, label: "On order" },
    { part: 20, label: "When the mockup is approved" },
    { part: 50, label: "At go-live, once you have approved the site" },
  ],
  wordpress: [
    { part: 30, label: "On order" },
    { part: 20, label: "When the work plan is approved, after the audit" },
    { part: 50, label: "At go-live, once you have approved the staging site" },
  ],
};

export function jalonsPaiement(kind: VitrineCatalogueKind, lang: VitrineLanguage = "fr") {
  return (lang === "en" ? JALONS_EN : JALONS)[kind];
}

export const SUPPORT_INCLUS_EN = "Fixes, small changes, questions about running the site.";

export const MAINTENANCE_HORAIRE_EN = {
  inclus: {
    standard: [
      "You choose the number of prepaid hours per month",
      "Changes, fixes and improvements to the site, according to your priorities",
      "Response within 24 hours for an urgent problem",
      "Unused hours roll over to the next month: they are not lost",
      "For any request estimated at more than 4 hours, I let you know before starting so we can agree on the priority and the hours it will use",
    ],
    wordpress: [
      "You choose the number of prepaid hours per month",
      "WordPress, theme and plugin updates tested before they are applied, fixes and changes, according to your priorities",
      "Response within 24 hours for an urgent problem",
      "Unused hours roll over to the next month: they are not lost",
      "For any request estimated at more than 4 hours, I let you know before starting so we can agree on the priority and the hours it will use",
    ],
  } satisfies Record<VitrineCatalogueKind, string[]>,
  engagement:
    "If a task takes longer than the estimate I gave you, I finish it without billing or counting the extra hours.",
};

export const HEBERGEMENT_CONTENU_EN =
  "Hosting on a managed server, security certificate, daily backups, security updates.";

/** Montant en euros dans la langue de la page ("1 234 €" ou "€1,234"). */
export function formatEurosLang(n: number, lang: VitrineLanguage = "fr"): string {
  if (lang !== "en") return formatEuros(n);
  return `€${new Intl.NumberFormat("en-GB").format(Math.round(n))}`;
}
